"use client";

import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { startTransition, useCallback, useEffect, useRef } from "react";

import { Button } from "@/components/buttons/Button";
import { ButtonLink } from "@/components/buttons/ButtonLink";
import { classifyRenderError } from "@/components/errorState/classifyRenderError";
import { ERROR_STATE_COPY } from "@/components/errorState/constants";
import { shouldCaptureRenderError } from "@/components/errorState/shouldCaptureRenderError";
import { useOnlineStatus } from "@/components/errorState/useOnlineStatus";
import { TextLink } from "@/components/links/TextLink";
import { Heading1 } from "@/components/text/Heading1";
import { OverHeading } from "@/components/text/OverHeading";
import { Preamble } from "@/components/text/Preamble";
import { ErrorBoundaryName, RenderError } from "@/types/defs/errorState";

const MAX_AUTOMATIC_RETRIES = 2;

export function ErrorState({
  error,
  reset,
  boundary,
}: {
  error: RenderError;
  reset: () => void;
  boundary: ErrorBoundaryName;
}) {
  const router = useRouter();
  const isOnline = useOnlineStatus();

  const variant = classifyRenderError(error, isOnline);
  const copy = ERROR_STATE_COPY[variant];

  // The capture decision is made once per error object, on first sight: an error that arrived while offline stays uncaptured when the connection returns and the variant flips to connection/server, because the auto-retry below fires in that same commit — if it fails again, that is a new error object and gets classified on its own. In production every server-component failure reaches the client with the same redacted message and no stack, so the digest is the only thing that tells them apart and the only link to the server-side capture.
  const handledErrorRef = useRef<RenderError | null>(null);
  useEffect(() => {
    if (handledErrorRef.current === error) {
      return;
    }
    handledErrorRef.current = error;
    if (!shouldCaptureRenderError(variant)) {
      return;
    }
    posthog.captureException(error, {
      boundary,
      errorScreenVariant: variant,
      digest: error.digest ?? null,
    });
  }, [error, variant, boundary]);

  // refresh re-fetches the server-component payload; reset alone would re-render the segment against the same failed data and fail again.
  const retryRender = useCallback(() => {
    startTransition(() => {
      router.refresh();
      reset();
    });
  }, [router, reset]);

  // The offline screen retries itself the moment the connection returns — no click needed. A flapping link (wifi/cellular handoff) would otherwise refetch and re-capture on every flip, so after a couple of automatic attempts the manual button takes over.
  const wasOfflineRef = useRef(!isOnline);
  const automaticRetriesRef = useRef(0);
  useEffect(() => {
    if (
      wasOfflineRef.current &&
      isOnline &&
      automaticRetriesRef.current < MAX_AUTOMATIC_RETRIES
    ) {
      automaticRetriesRef.current++;
      retryRender();
    }
    wasOfflineRef.current = !isOnline;
  }, [isOnline, retryRender]);

  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center px-4 py-24 text-center"
    >
      {variant === "offline" ?
        <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-1 text-xs font-semibold text-amber-700">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          {copy.overHeading}
        </span>
      : <OverHeading elementType="p">{copy.overHeading}</OverHeading>}

      <Heading1 className="mt-4" elementType="h1">
        {copy.heading}
      </Heading1>
      <Preamble className="mt-6 max-w-xl">{copy.body}</Preamble>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-4">
        {variant === "offline" && (
          // A button that can't work shouldn't look like it can — disabled until the connection returns, then the retry fires on its own.
          <Button variant="primary" loading>
            Waiting for connection…
          </Button>
        )}
        {variant === "connection" && (
          <>
            <Button variant="primary" onClick={retryRender}>
              Try again
            </Button>
            <TextLink href="/contact" external={false}>
              Contact support →
            </TextLink>
          </>
        )}
        {variant === "server" && (
          <>
            <Button variant="primary" onClick={retryRender}>
              Try again
            </Button>
            <ButtonLink href="/" external={false}>
              Back to home
            </ButtonLink>
          </>
        )}
      </div>
    </div>
  );
}
